/**
 * InlineRegisterRow — registrar sem modal (spec `2026-08-14-aba-acoes-conversa-em-curso-design.md`
 * §5.4; mockup `2a` do projeto Claude Design da aba Ações).
 *
 * **UM campo.** Prioridade não se pergunta: nasce `medium` e muda no `···` do cartão.
 * O diagnóstico que originou esta linha é que o caminho manual anterior (modal com
 * título + select de prioridade + `datetime-local`) produzia registro INÚTIL mesmo
 * quando preenchido — numa operação medida em segundos, o operador salva com prazo em
 * branco, e registro sem prazo não cobra ninguém.
 *
 * O prazo sai da própria frase (`parseDeadlinePhrase`) e aparece RESOLVIDO ao lado
 * antes de gravar. Nunca se grava data que o operador não viu.
 *
 * O alternador **eu prometi / o cliente** é o que grava o `source` — o campo que
 * distingue quem prometeu, sem tabela nova (vide `@lume/shared` `pendencies/provenance.ts`,
 * promovido do web em 15/Ago). É também
 * o produtor de `customer-promise` desde o primeiro dia: a captura automática do lado
 * do cliente é rodada seguinte, mas a secretária que anota «a paciente traz o
 * encaminhamento na quinta» já é atendida aqui.
 *
 * O modal completo (`PendencyCreateModal`, aberto pelo header da conversa) continua
 * existindo para quem quiser todos os campos — este é o caminho curto, não o único.
 */

import { parseDeadlinePhrase } from '@lume/shared';
import { Check, Plus } from 'lucide-react';
import { useState } from 'react';


import { useCreatePendency } from '../../../../hooks/use-pendencies';
import { useToastStore } from '../../../../stores/toast-store';

/** Quem prometeu — grava o `source` da Pendency. */
type Who = 'me' | 'client';

const SOURCE_BY_WHO: Record<Who, string> = {
  me: 'conversation-promise',
  client: 'customer-promise',
};

export function InlineRegisterRow({
  conversationId,
  onDone,
}: {
  conversationId: string;
  /** Chamado no sucesso e no Esc — o pai fecha a linha. */
  onDone: () => void;
}): JSX.Element {
  const create = useCreatePendency();
  const [text, setText] = useState('');
  const [who, setWho] = useState<Who>('me');

  const match = parseDeadlinePhrase(text, new Date());
  const canSubmit = text.trim().length > 0 && !create.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    create.mutate(
      {
        conversationId,
        title: text.trim(),
        priority: 'medium',
        source: SOURCE_BY_WHO[who],
        ...(match !== null && { deadline: match.at.toISOString() }),
      },
      {
        onSuccess: () => {
          setText('');
          onDone();
        },
        onError: (err) => {
          useToastStore.getState().push({
            variant: 'error',
            title: 'Não foi possível registrar',
            description: err instanceof Error ? err.message : 'Tente novamente.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  return (
    <div className="inline-register" data-testid="inline-register">
      <div className="inline-register__input">
        <Plus className="icon" size={12} aria-hidden />
        {/* `autoFocus` é intencional: a linha só existe porque o operador acabou de
            clicar em «+ registrar». Pedir um segundo clique para digitar seria
            exatamente a fricção que esta linha existe para remover. */}
        <input
          autoFocus
          className="inline-register__field"
          placeholder="o que ficou de fazer?"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onDone();
            if (e.key === 'Enter') submit();
          }}
          maxLength={200}
        />
      </div>

      <div className="inline-register__row">
        <div className="seg" role="group" aria-label="quem prometeu">
          <button
            type="button"
            className={who === 'me' ? 'seg__b is-on' : 'seg__b'}
            aria-pressed={who === 'me'}
            onClick={() => setWho('me')}
          >
            eu prometi
          </button>
          <button
            type="button"
            className={who === 'client' ? 'seg__b is-on' : 'seg__b'}
            aria-pressed={who === 'client'}
            onClick={() => setWho('client')}
          >
            o cliente
          </button>
        </div>

        {match !== null && (
          <span className="inline-register__parsed" data-testid="parsed-deadline">
            <Check size={10} aria-hidden /> {formatParsed(match.at)}
          </span>
        )}

        <span style={{ flex: 1 }} />

        <button
          type="button"
          className="btn btn--primary btn--xs"
          onClick={submit}
          disabled={!canSubmit}
        >
          Registrar
        </button>
      </div>
    </div>
  );
}

/** «qui, 21/08 · 18:00» — o que o operador confere antes de gravar. */
function formatParsed(at: Date): string {
  const day = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(at);
  const time = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(at);
  return `${day} · ${time}`;
}
